import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader } from "@/components/Loader";
import { ArrowRight } from "lucide-react";
import { Link } from "wouter";
import type { GrowthEfficiencyV2Analysis } from "./growthEfficiencyV2";
import { formatRevenuePerHour } from "../lib/moneyDisplay";
import { salesEconomicsReadinessMessages, type SalesEconomicsReadiness } from "./salesEconomicsReadiness";
import { SALES_ECONOMICS_READINESS_COPY as COPY } from "./salesEconomicsCopy";

function roundedShare(value: number | null) {
  return value === null ? null : Math.round(value * 100);
}

function comparisonLabel(value: number | null) {
  if (value === null) return null;
  if (Math.abs(value) < 0.005) return "In line with the previous 90 days";
  return `${Math.round(Math.abs(value) * 100)}% ${value > 0 ? "higher" : "lower"} than the previous 90 days`;
}

export function GrowthEfficiencyOverviewCard({
  analysis,
  companyCurrency,
  error,
  href,
  isLoading,
  open,
  readiness,
}: {
  analysis: GrowthEfficiencyV2Analysis | null;
  companyCurrency: Parameters<typeof formatRevenuePerHour>[1];
  error: unknown;
  href: string;
  isLoading: boolean;
  open: () => void;
  readiness?: SalesEconomicsReadiness | null;
}) {
  const lostShare = roundedShare(analysis?.outcomeEffort.lostShare ?? null);
  const wonShare = lostShare === null ? null : 100 - lostShare;
  const revenuePerHour = analysis?.revenuePerSalesHour ?? null;
  const revenueChange = comparisonLabel(analysis?.revenuePerSalesHourChange ?? null);

  return (
    <Card
      className="flex h-full cursor-pointer flex-col border-[#5E548E]/35 bg-[#5E548E]/[0.025] transition-colors hover:border-[#5E548E]/65 hover:bg-[#5E548E]/[0.05]"
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        open();
      }}
      data-href={href}
    >
      <CardHeader className="p-5 pb-3">
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="text-base">Sales Economics</CardTitle>
          <Link
            href={href}
            className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-[#5E548E] hover:text-[#231942]"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            Open Sales Economics
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </CardHeader>
      <CardContent className="flex-1 p-5 pt-0">
        {isLoading ? (
          <div
            className="flex min-h-[145px] items-center justify-center"
            aria-label="Loading Sales Economics"
          >
            <Loader size={24} />
          </div>
        ) : !error && analysis && readiness && !readiness.ready ? (
          <div className="space-y-3 py-4">
            <p className="text-lg font-semibold leading-6 text-[#231942]">{COPY.title}</p>
            <p className="text-xs text-gray-500">{readiness.current.complete} of {readiness.current.population} closed deals have complete activity.</p>
            <p className="text-xs text-gray-500">{salesEconomicsReadinessMessages(readiness.issues)[0] ?? COPY.title}</p>
          </div>
        ) : error || !analysis || !readiness?.ready || lostShare === null || wonShare === null ? (
          <div className="flex min-h-[145px] items-center justify-center rounded-[8px] border border-dashed border-gray-200 px-4 text-center text-xs text-gray-500">
            More complete closed-deal activity is needed for Sales Economics.
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <p className="text-lg font-semibold leading-6 text-[#231942]">
                {lostShare}% of sales time went to lost deals
              </p>
              <p className="mt-1 text-xs text-gray-500">Past 90 days</p>
            </div>

            <div>
              <div
                className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100"
                role="img"
                aria-label={`${wonShare}% of sales time on won deals and ${lostShare}% on lost deals`}
              >
                <span className="bg-[#1D819A]" style={{ width: `${wonShare}%` }} />
                <span className="bg-[#B13E4B]" style={{ width: `${lostShare}%` }} />
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px] tabular-nums">
                <span className="text-[#1D819A]">Won {wonShare}%</span>
                <span className="text-[#B13E4B]">Lost {lostShare}%</span>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <p className="text-[11px] font-medium text-gray-500">Revenue per sales hour</p>
              <div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <p className="text-lg font-semibold tabular-nums text-[#231942]">
                  {revenuePerHour === null
                    ? "—"
                    : formatRevenuePerHour(revenuePerHour, companyCurrency)}
                </p>
                {revenueChange && (
                  <p className="text-[11px] text-gray-500">{revenueChange}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
